import { useCallback, useMemo, useState } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'

export function computePagination(page: number, total: number, size: number) {
  const totalPages = Math.max(1, Math.ceil(total / size))
  const safePage = Math.min(Math.max(1, page), totalPages)
  const rangeFrom = total === 0 ? 0 : (safePage - 1) * size + 1
  const rangeTo = Math.min(safePage * size, total)
  return { totalPages, safePage, rangeFrom, rangeTo }
}

export type SortOrder = 'asc' | 'desc' | null
export interface SortState {
  sortBy: string | null
  order: SortOrder
}

export interface SortQuery {
  sortBy?: string
  order?: 'ASC' | 'DESC'
}

export function useSortState(initial: SortState = { sortBy: null, order: null }) {
  const [sort, setSort] = useState<SortState>(initial)
  const sortQuery: SortQuery = useMemo(
    () =>
      sort.sortBy && sort.order
        ? { sortBy: sort.sortBy, order: sort.order.toUpperCase() as 'ASC' | 'DESC' }
        : {},
    [sort.sortBy, sort.order],
  )
  return { sort, setSort, sortQuery }
}

export const demoButtonClass = {
  secondary: 'px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition cursor-pointer font-medium text-sm whitespace-nowrap',
  primary: 'px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md cursor-pointer font-medium text-sm whitespace-nowrap',
  primarySm: 'px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md cursor-pointer font-medium text-sm whitespace-nowrap',
  success: 'px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-md cursor-pointer font-medium text-sm whitespace-nowrap',
  orange: 'px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition shadow-md cursor-pointer font-medium text-sm whitespace-nowrap',
  dark: 'inline-flex items-center px-5 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition shadow-md cursor-pointer whitespace-nowrap',
  darkShrink: 'inline-flex items-center shrink-0 px-5 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition shadow-md cursor-pointer whitespace-nowrap',
  slate: 'inline-flex items-center px-5 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition shadow-md cursor-pointer whitespace-nowrap',
  sky: 'px-5 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition shadow-md cursor-pointer font-medium text-sm whitespace-nowrap',
  indigo: 'px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-md cursor-pointer font-medium text-sm whitespace-nowrap',
  violet: 'px-5 py-2 bg-violet-800 text-white rounded-lg hover:bg-violet-900 transition shadow-md cursor-pointer font-medium text-sm whitespace-nowrap',
  outline: 'px-5 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition cursor-pointer font-medium text-sm whitespace-nowrap',
  outlineBlue: 'px-5 py-2 bg-white border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition shadow-sm cursor-pointer font-medium text-sm',
  outlineDanger: 'px-5 py-2 bg-white border border-red-300 text-red-800 rounded-lg hover:bg-red-50 transition cursor-pointer font-medium text-sm whitespace-nowrap',
  linkBlue: 'text-blue-600 text-sm font-medium hover:underline cursor-pointer whitespace-nowrap',
  linkGreen: 'text-green-600 text-sm font-medium hover:underline cursor-pointer whitespace-nowrap',
  linkOrange: 'text-orange-700 text-sm font-medium hover:underline cursor-pointer whitespace-nowrap',
  linkRed: 'text-red-600 text-sm font-medium hover:underline cursor-pointer whitespace-nowrap',
  linkSlate: 'text-slate-700 text-sm font-medium hover:underline cursor-pointer whitespace-nowrap',
  disabled: 'px-5 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed shadow-md font-medium text-sm whitespace-nowrap',
} as const

export type ColumnWidthMap = Record<string, number>

export interface UseResizableColumnsOptions {
  defaults: ColumnWidthMap
  minWidth?: number
}

export function useResizableColumns({ defaults, minWidth = 48 }: UseResizableColumnsOptions) {
  const [overrides, setOverrides] = useState<ColumnWidthMap>({})
  const widths: ColumnWidthMap = useMemo(
    () => ({ ...defaults, ...overrides }),
    [defaults, overrides],
  )

  const startResize = useCallback(
    (columnId: string, e: ReactMouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const startX = e.clientX
      const startWidth = overrides[columnId] ?? defaults[columnId] ?? 100

      const onMove = (ev: MouseEvent) => {
        const next = Math.max(minWidth, startWidth + ev.clientX - startX)
        setOverrides((prev) => ({ ...prev, [columnId]: next }))
      }
      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [overrides, defaults, minWidth],
  )

  return { widths, startResize }
}
